import { useState, type FormEvent } from "react";
import {
  FIELD_LIMITS,
  SHOOT_TYPE_LABELS,
  ShootType,
  type BookingSubmission,
} from "@photography-booking/shared";
import {
  collectFieldErrors,
  DURATION_OPTIONS,
  DEFAULT_DURATION_MINUTES,
  type FieldErrors,
} from "../lib/booking-form.js";
import type { SubmitBookingResult } from "../api/client.js";
import {
  trackBookingSubmitted,
  trackClickThrough,
  trackFormStarted,
} from "../lib/use-site-tracker.js";

interface BookingFormProps {
  /** The date chosen on the calendar, or null until one is selected. */
  selectedDate: string | null;
  /** Submits the validated inquiry; resolves with the API outcome. */
  onSubmit: (submission: BookingSubmission) => Promise<SubmitBookingResult>;
}

/** Local editable fields (the requested date is owned by the calendar). */
interface FormState {
  clientName: string;
  clientEmail: string;
  /** Session start time as a 24h "HH:MM" value (from <input type="time">). */
  startTime: string;
  /** Session length in minutes, held as the raw <select> string value. */
  durationMinutes: string;
  /** Requested shoot type ("" until the visitor picks one). */
  shootType: string;
  message: string;
  /** Honeypot — hidden field; real users leave it blank, bots fill it. */
  website: string;
}

const EMPTY_FORM: FormState = {
  clientName: "",
  clientEmail: "",
  startTime: "",
  durationMinutes: String(DEFAULT_DURATION_MINUTES),
  shootType: "",
  message: "",
  website: "",
};

/** Shoot-type options in display order for the booking form dropdown. */
const SHOOT_TYPE_OPTIONS: readonly ShootType[] = [
  ShootType.SWIM,
  ShootType.FASHION,
  ShootType.OTHER,
];

/**
 * The start-time picker state: a two-part 12-hour time (hour + minute, no
 * built-in AM/PM segment) plus an explicit AM/PM dropdown. `form.startTime`
 * stays the canonical 24h "HH:MM" derived from these three.
 */
interface TimeParts {
  /** Hour on a 12-hour clock, "1".."12" (raw digits as typed). */
  hour: string;
  /** Minute, "0".."59" (raw digits as typed). */
  minute: string;
  meridiem: "" | "AM" | "PM";
}

const EMPTY_TIME_PARTS: TimeParts = { hour: "", minute: "", meridiem: "" };

/**
 * Converts the hour + minute + AM/PM into the canonical 24h "HH:MM" the form
 * submits/validates. Returns "" until all three are present and valid (hour
 * 1–12, minute 0–59), so an incomplete entry surfaces as the normal "required"
 * validation error.
 */
function timePartsTo24h(parts: TimeParts): string {
  if (parts.hour === "" || parts.minute === "" || !parts.meridiem) {
    return "";
  }
  const hour12 = Number(parts.hour);
  const minute = Number(parts.minute);
  if (
    !Number.isInteger(hour12) ||
    !Number.isInteger(minute) ||
    hour12 < 1 ||
    hour12 > 12 ||
    minute < 0 ||
    minute > 59
  ) {
    return "";
  }
  let hour = hour12 % 12;
  if (parts.meridiem === "PM") {
    hour += 12;
  }
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/**
 * The "Let's get started!" booking inquiry form (Req 8.2, 8.3).
 *
 * Validation runs entirely through the shared `validateBookingSubmission` (via
 * {@link collectFieldErrors}), so the client enforces exactly the rules the
 * server does — required fields, a valid email format, and per-field length
 * limits — and surfaces a precise message beside each offending field before
 * the request is sent. The requested date comes from the availability picker,
 * so an unselected date shows as a missing-required-field error. The layout is
 * single-column and fluid for cross-device usability (Req 6.7).
 *
 * Engagement measurement rides along on three points — the first field edit,
 * the CTA activation, and the accepted submission — and every call is a
 * no-throw boundary, so it can never break the submit path (Req 2.4–2.6, 14.5).
 */
export function BookingForm({
  selectedDate,
  onSubmit,
}: BookingFormProps): JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  // The friendly 12-hour + AM/PM picker state; `form.startTime` stays the
  // canonical 24h "HH:MM" value derived from these parts.
  const [timeParts, setTimeParts] = useState<TimeParts>(EMPTY_TIME_PARTS);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [shootTypeError, setShootTypeError] = useState<string | null>(null);
  const [confirmedDate, setConfirmedDate] = useState<string | null>(null);

  const buildSubmission = (): BookingSubmission => {
    // The duration <select> only offers integer options, but parse defensively
    // and let the shared validator reject a NaN as an invalid duration.
    const parsedDuration = Number.parseInt(form.durationMinutes, 10);
    return {
      clientName: form.clientName,
      clientEmail: form.clientEmail,
      requestedDate: selectedDate ?? "",
      startTime: form.startTime,
      durationMinutes: Number.isNaN(parsedDuration) ? undefined : parsedDuration,
      shootType: form.shootType || undefined,
      message: form.message,
      website: form.website || undefined,
    };
  };

  const update = (field: keyof FormState, value: string): void => {
    // First edit of any field starts the form for measurement purposes; later
    // edits are no-ops inside the tracker (Req 2.5). Cannot throw (Req 14.5).
    trackFormStarted();
    setForm((current) => ({ ...current, [field]: value }));
    // The shoot-type error is tracked separately (it is not part of the shared
    // field-error map), so clear it here and skip the field-error handling.
    if (field === "shootType") {
      setShootTypeError(null);
      return;
    }
    // Clear a field's error as soon as the visitor edits it; the full
    // re-validation happens on submit.
    setErrors((current) => {
      const key = field as keyof FieldErrors;
      if (current[key] === undefined) {
        return current;
      }
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  /**
   * Updates one part of the hour / minute / AM-PM picker and recomputes the
   * canonical 24h `startTime` (blank until all three are valid). Hour and
   * minute inputs are sanitized to at most two digits.
   */
  const updateTimePart = (part: keyof TimeParts, value: string): void => {
    const cleaned =
      part === "hour" || part === "minute"
        ? value.replace(/\D/g, "").slice(0, 2)
        : value;
    const next = { ...timeParts, [part]: cleaned } as TimeParts;
    setTimeParts(next);
    update("startTime", timePartsTo24h(next));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    // The "Let's get started!" button is the booking call to action; record the
    // activation before anything else can short-circuit the submit (Req 2.4).
    trackClickThrough("booking:submit");
    setSubmitError(null);

    // A missing calendar selection is the most common slip. Surface a clear,
    // plain-language prompt pointing at the calendar rather than the cryptic
    // "requestedDate is required" field error (there is no visible field by
    // that name).
    if (!selectedDate) {
      setErrors({});
      setSubmitError(
        "Please choose an available date on the calendar before submitting.",
      );
      return;
    }

    // Require a shoot-type selection (the server treats it as optional and
    // defaults to "Other", but the form asks the visitor to choose).
    if (!form.shootType) {
      setShootTypeError("Please choose a shoot type.");
      return;
    }
    setShootTypeError(null);

    const submission = buildSubmission();
    const fieldErrors = collectFieldErrors(submission);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      const result = await onSubmit(submission);
      if (result.ok) {
        // Success branch of `POST /bookings` (Req 2.6). Measurement only: it
        // cannot throw, so the confirmation always renders (Req 14.5).
        trackBookingSubmitted();
        setConfirmedDate(result.requestedDate);
        // Preserve the client's identity so a returning visitor submitting
        // another inquiry doesn't have to re-enter their name/email; only the
        // session-specific fields are cleared.
        setForm({
          ...EMPTY_FORM,
          clientName: form.clientName,
          clientEmail: form.clientEmail,
        });
        setTimeParts(EMPTY_TIME_PARTS);
        return;
      }

      // Map any server-side validation errors back onto their fields so the
      // client stays in lock-step with the authoritative validation.
      if (result.validationErrors && result.validationErrors.length > 0) {
        const mapped: FieldErrors = {};
        for (const error of result.validationErrors) {
          const field = error.field as keyof FieldErrors;
          if (mapped[field] === undefined) {
            mapped[field] = error.message;
          }
        }
        setErrors(mapped);
      }
      setSubmitError(result.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmedDate) {
    return (
      <div className="booking-form booking-form--confirmed" role="status">
        <h3 className="booking-form__confirm-title">Thank you</h3>
        <p className="booking-form__confirm-body">
          Your inquiry for <strong>{confirmedDate}</strong> has been received.
          Check your inbox for a confirmation email — we'll be in touch soon.
        </p>
        <button
          type="button"
          className="booking-form__reset"
          onClick={() => setConfirmedDate(null)}
        >
          Submit another inquiry
        </button>
      </div>
    );
  }

  return (
    <form className="booking-form" onSubmit={handleSubmit} noValidate>
      {/* Honeypot: hidden from real users (and screen readers). Bots that fill
          every field will populate it, and the server silently drops those. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <label htmlFor="booking-website">Website (leave blank)</label>
        <input
          id="booking-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(event) => update("website", event.target.value)}
        />
      </div>

      <p className="booking-form__date" aria-live="polite">
        {selectedDate ? (
          <>
            Requested date: <strong>{selectedDate}</strong>
          </>
        ) : (
          <span className="booking-form__date-hint">
            Pick an available date from the calendar to begin.
          </span>
        )}
      </p>
      {errors.requestedDate ? (
        <p className="booking-form__error" role="alert">
          {errors.requestedDate}
        </p>
      ) : null}

      <div className="booking-form__field">
        <label className="booking-form__label" htmlFor="booking-name">
          Name
        </label>
        <input
          id="booking-name"
          className="booking-form__input"
          type="text"
          autoComplete="name"
          maxLength={FIELD_LIMITS.clientName}
          value={form.clientName}
          aria-invalid={errors.clientName !== undefined}
          aria-describedby={
            errors.clientName ? "booking-name-error" : undefined
          }
          onChange={(event) => update("clientName", event.target.value)}
        />
        {errors.clientName ? (
          <p id="booking-name-error" className="booking-form__error" role="alert">
            {errors.clientName}
          </p>
        ) : null}
      </div>

      <div className="booking-form__field">
        <label className="booking-form__label" htmlFor="booking-email">
          Email
        </label>
        <input
          id="booking-email"
          className="booking-form__input"
          type="email"
          autoComplete="email"
          maxLength={FIELD_LIMITS.clientEmail}
          value={form.clientEmail}
          aria-invalid={errors.clientEmail !== undefined}
          aria-describedby={
            errors.clientEmail ? "booking-email-error" : undefined
          }
          onChange={(event) => update("clientEmail", event.target.value)}
        />
        {errors.clientEmail ? (
          <p
            id="booking-email-error"
            className="booking-form__error"
            role="alert"
          >
            {errors.clientEmail}
          </p>
        ) : null}
      </div>

      <div className="booking-form__field">
        <label className="booking-form__label" htmlFor="booking-start-hour">
          Start time
        </label>
        <div
          className="booking-form__time"
          aria-invalid={errors.startTime !== undefined}
          aria-describedby={
            errors.startTime ? "booking-start-time-error" : undefined
          }
        >
          <input
            id="booking-start-hour"
            className="booking-form__input booking-form__time-part"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={2}
            placeholder="HH"
            aria-label="Start hour"
            value={timeParts.hour}
            onChange={(event) => updateTimePart("hour", event.target.value)}
          />
          <span className="booking-form__time-sep" aria-hidden="true">
            :
          </span>
          <input
            className="booking-form__input booking-form__time-part"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={2}
            placeholder="MM"
            aria-label="Start minutes"
            value={timeParts.minute}
            onChange={(event) => updateTimePart("minute", event.target.value)}
          />
          <select
            className="booking-form__input booking-form__time-meridiem"
            aria-label="AM or PM"
            value={timeParts.meridiem}
            onChange={(event) => updateTimePart("meridiem", event.target.value)}
          >
            <option value="">AM/PM</option>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
        {errors.startTime ? (
          <p
            id="booking-start-time-error"
            className="booking-form__error"
            role="alert"
          >
            {errors.startTime}
          </p>
        ) : null}
      </div>

      <div className="booking-form__field">
        <label className="booking-form__label" htmlFor="booking-duration">
          Session length
        </label>
        <select
          id="booking-duration"
          className="booking-form__input"
          value={form.durationMinutes}
          aria-invalid={errors.durationMinutes !== undefined}
          aria-describedby={
            errors.durationMinutes ? "booking-duration-error" : undefined
          }
          onChange={(event) => update("durationMinutes", event.target.value)}
        >
          {DURATION_OPTIONS.map((option) => (
            <option key={option.minutes} value={String(option.minutes)}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.durationMinutes ? (
          <p
            id="booking-duration-error"
            className="booking-form__error"
            role="alert"
          >
            {errors.durationMinutes}
          </p>
        ) : null}
      </div>

      <div className="booking-form__field">
        <label className="booking-form__label" htmlFor="booking-shoot-type">
          Shoot type
        </label>
        <select
          id="booking-shoot-type"
          className="booking-form__input"
          value={form.shootType}
          aria-invalid={shootTypeError !== null}
          aria-describedby={
            shootTypeError ? "booking-shoot-type-error" : undefined
          }
          onChange={(event) => update("shootType", event.target.value)}
        >
          <option value="">Select a shoot type…</option>
          {SHOOT_TYPE_OPTIONS.map((type) => (
            <option key={type} value={type}>
              {SHOOT_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
        <p className="booking-form__date-hint">
          Swimwear and fashion shoots. For anything else choose “Other” and
          describe it below.
        </p>
        {shootTypeError ? (
          <p
            id="booking-shoot-type-error"
            className="booking-form__error"
            role="alert"
          >
            {shootTypeError}
          </p>
        ) : null}
      </div>

      <div className="booking-form__field">
        <label className="booking-form__label" htmlFor="booking-message">
          Tell us about your session
        </label>
        <p className="booking-form__date-hint">
          For example, share the shoot location, the looks or outfits you have in
          mind, references, or anything else that helps plan the session.
        </p>
        <textarea
          id="booking-message"
          className="booking-form__input booking-form__textarea"
          rows={5}
          maxLength={FIELD_LIMITS.message}
          placeholder="e.g. Location, outfit ideas, references, or any questions…"
          value={form.message}
          aria-invalid={errors.message !== undefined}
          aria-describedby={
            errors.message ? "booking-message-error" : undefined
          }
          onChange={(event) => update("message", event.target.value)}
        />
        {errors.message ? (
          <p
            id="booking-message-error"
            className="booking-form__error"
            role="alert"
          >
            {errors.message}
          </p>
        ) : null}
      </div>

      {submitError ? (
        <p className="booking-form__error booking-form__error--submit" role="alert">
          {submitError}
        </p>
      ) : null}

      {!selectedDate ? (
        <p className="booking-form__date-hint" aria-live="polite">
          Select an available date on the calendar to enable your request.
        </p>
      ) : null}

      <button
        type="submit"
        className="booking-form__submit"
        disabled={submitting}
      >
        {submitting ? "Sending…" : "Let's get started!"}
      </button>
    </form>
  );
}
